/**
 * Request Viewing Modal Component
 * Allows public users to request property viewings
 */

import React, { useState } from "react";
import { useToast } from "../../contexts/ToastContext";
import { useCreatePublicViewingRequestMutation } from "../../store/api/propertyViewingsApi";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select from "../ui/Select";

interface RequestViewingModalProps {
	propertyId: string;
	propertyName?: string;
	isOpen?: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

const RequestViewingModal: React.FC<RequestViewingModalProps> = ({
	propertyId,
	propertyName,
	isOpen = true,
	onClose,
	onSuccess,
}) => {
	if (!isOpen) return null;
	const { success, error: showError } = useToast();
	const [createRequest, { isLoading: isSubmitting }] =
		useCreatePublicViewingRequestMutation();
	const [formData, setFormData] = useState({
		buyer_name: "",
		buyer_email: "",
		buyer_phone: "",
		preferred_date: "",
		preferred_time: "09:00",
		duration: "30",
		notes: "",
	});

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	// Generate time options (9 AM to 6 PM in 30-minute intervals)
	const generateTimeOptions = () => {
		const options = [];
		for (let hour = 9; hour <= 18; hour++) {
			for (let minute = 0; minute < 60; minute += 30) {
				const time = `${hour.toString().padStart(2, "0")}:${minute
					.toString()
					.padStart(2, "0")}`;
				const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString(
					"en-US",
					{
						hour: "numeric",
						minute: "2-digit",
						hour12: true,
					},
				);
				options.push({ value: time, label: displayTime });
			}
		}
		return options;
	};

	const durationOptions = [
		{ value: "15", label: "15 minutes" },
		{ value: "30", label: "30 minutes" },
		{ value: "45", label: "45 minutes" },
		{ value: "60", label: "1 hour" },
		{ value: "90", label: "1.5 hours" },
		{ value: "120", label: "2 hours" },
	];

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields
		if (!formData.buyer_name.trim()) {
			showError("Please enter your name.");
			return;
		}

		if (!formData.preferred_date) {
			showError("Please select a preferred date.");
			return;
		}

		// Validate email format if provided
		if (formData.buyer_email) {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(formData.buyer_email)) {
				showError("Please enter a valid email address.");
				return;
			}
		}

		try {
			await createRequest({
				property: propertyId,
				buyer_name: formData.buyer_name.trim(),
				buyer_email: formData.buyer_email.trim() || undefined,
				buyer_phone: formData.buyer_phone.trim() || undefined,
				preferred_date: formData.preferred_date,
				preferred_time: formData.preferred_time,
				duration: parseInt(formData.duration),
				notes: formData.notes.trim() || undefined,
			}).unwrap();

			success(
				"Viewing request submitted successfully! We'll contact you soon to confirm the appointment.",
			);
			onSuccess?.();
			onClose();
		} catch (error: any) {
			console.error("Failed to submit viewing request:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to submit viewing request. Please try again.",
			);
		}
	};

	// Get minimum date (today)
	const today = new Date().toISOString().split("T")[0];

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
			<div className="bg-card rounded-lg border border-border w-full max-w-2xl my-auto">
				<div className="flex items-center justify-between p-6 border-b border-border">
					<div>
						<h2 className="text-xl font-semibold text-foreground">
							Request Property Viewing
						</h2>
						{propertyName && (
							<p className="text-sm text-muted-foreground mt-1">
								{propertyName}
							</p>
						)}
					</div>
					<Button variant="ghost" size="icon" onClick={onClose}>
						<Icon name="X" size={20} />
					</Button>
				</div>

				<form onSubmit={handleSubmit} className="p-6 space-y-6">
					{/* Contact Information */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-foreground flex items-center gap-2">
							<Icon name="User" size={20} />
							Your Information
						</h3>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Full Name *
								</label>
								<Input
									value={formData.buyer_name}
									onChange={(e) =>
										handleInputChange("buyer_name", e.target.value)
									}
									placeholder="John Smith"
									required
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Email
								</label>
								<Input
									type="email"
									value={formData.buyer_email}
									onChange={(e) =>
										handleInputChange("buyer_email", e.target.value)
									}
									placeholder="john@example.com"
								/>
							</div>

							<div className="md:col-span-2">
								<label className="block text-sm font-medium text-foreground mb-2">
									Phone Number
								</label>
								<Input
									type="tel"
									value={formData.buyer_phone}
									onChange={(e) =>
										handleInputChange("buyer_phone", e.target.value)
									}
									placeholder="(555) 123-4567"
								/>
							</div>
						</div>
					</div>

					{/* Preferred Viewing Time */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-foreground flex items-center gap-2">
							<Icon name="Calendar" size={20} />
							Preferred Viewing Time
						</h3>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Date *
								</label>
								<Input
									type="date"
									value={formData.preferred_date}
									onChange={(e) =>
										handleInputChange("preferred_date", e.target.value)
									}
									min={today}
									required
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Time
								</label>
								<Select
									value={formData.preferred_time}
									onValueChange={(value) =>
										handleInputChange("preferred_time", value as string)
									}
									options={generateTimeOptions()}
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Duration
								</label>
								<Select
									value={formData.duration}
									onValueChange={(value) =>
										handleInputChange("duration", value as string)
									}
									options={durationOptions}
								/>
							</div>
						</div>
					</div>

					{/* Additional Notes */}
					<div>
						<label className="block text-sm font-medium text-foreground mb-2">
							Additional Notes (Optional)
						</label>
						<textarea
							value={formData.notes}
							onChange={(e) => handleInputChange("notes", e.target.value)}
							rows={3}
							className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
							placeholder="Any special requests or questions..."
						/>
					</div>

					{/* Info Message */}
					<div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
						<p className="text-sm text-foreground flex items-start gap-2">
							<Icon name="Info" size={16} className="mt-0.5 flex-shrink-0" />
							<span>
								Your viewing request will be reviewed by our team. We'll contact
								you shortly to confirm the appointment time and provide further
								details.
							</span>
						</p>
					</div>

					{/* Form Actions */}
					<div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
						<Button type="button" variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" disabled={isSubmitting}>
							{isSubmitting ? (
								<>
									<Icon
										name="Loader2"
										size={16}
										className="mr-2 animate-spin"
									/>
									Submitting...
								</>
							) : (
								"Submit Request"
							)}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default RequestViewingModal;
